import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '../components/ui/Breadcrumbs';

interface UseBreadcrumbsOptions {
  customerId?: number;
  customerName?: string;
  siteId?: number | string;
  siteName?: string;
  virtualTenantId?: number | string;
  virtualTenantName?: string;
  alarmId?: number | string;
  currentPageTitle?: string;
}

// 🔹 Helpers
const buildCustomerBreadcrumb = (
  pathname: string,
  customerId?: number,
  customerName?: string
): BreadcrumbItem[] | null => {
  if (pathname.includes('/customers/') && customerId) {
    return [
      { label: 'Home', path: '/' },
      {
        label: customerName || `Customer ${customerId}`,
        path: `/customers/${customerId}`,
        isActive: true,
      },
    ];
  }
  return null;
};

const buildSiteBreadcrumb = (
  pathname: string,
  customerId?: number,
  customerName?: string,
  siteId?: number | string,
  siteName?: string
): BreadcrumbItem[] | null => {
  if (pathname.includes('/sites/') && siteId) {
    const items: BreadcrumbItem[] = [{ label: 'Home', path: '/' }];

    if (customerId && customerName) {
      items.push({
        label: customerName,
        path: `/customers/${customerId}`,
      });
    }

    items.push({
      label: siteName || `Site ${siteId}`,
      path: `/sites/${siteId}`,
      isActive: true,
    });

    return items;
  }
  return null;
};

const buildVirtualTenantBreadcrumb = (
  pathname: string,
  options: UseBreadcrumbsOptions
): BreadcrumbItem[] | null => {
  const {
    customerId,
    customerName,
    siteId,
    siteName,
    virtualTenantId,
    virtualTenantName,
  } = options;

  if (pathname.includes('/virtualTenants/vt/') && virtualTenantId) {
    const items: BreadcrumbItem[] = [{ label: 'Home', path: '/' }];

    if (customerId && customerName) {
      items.push({ label: customerName, path: `/customers/${customerId}` });
    }

    if (siteId && siteName) {
      items.push({ label: siteName, path: `/sites/${siteId}` });
    }

    items.push({
      label: virtualTenantName || `Virtual Tenant ${virtualTenantId}`,
      path: `/virtualTenants/vt/${virtualTenantId}`,
      isActive: true,
    });

    return items;
  }

  return null;
};

const buildManageBreadcrumb = (pathname: string): BreadcrumbItem[] | null => {
  if (!pathname.includes('/manage')) {return null;}

  const items: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Manage', path: '/manage' },
  ];

  const routes: Record<string, string> = {
    '/buildings': 'Buildings',
    '/distribution-groups': 'Distribution Groups',
    '/attachment-groups': 'Attachment Groups',
    '/schedules': 'Schedules',
    '/pending': 'Pending',
  };

  const match = Object.entries(routes).find(([route]) =>
    pathname.includes(route)
  );

  if (match) {
    items.push({
      label: match[1],
      path: `/manage${match[0]}`,
      isActive: true,
    });
  } else {
    items[items.length - 1].isActive = true;
  }

  return items;
};

const buildSimpleBreadcrumb = (
  pathname: string,
  path: string,
  label: string
): BreadcrumbItem[] | null => {
  if (pathname.includes(path)) {
    return [
      { label: 'Home', path: '/' },
      { label, path, isActive: true },
    ];
  }
  return null;
};

export function useBreadcrumbs(
  options: UseBreadcrumbsOptions = {}
): BreadcrumbItem[] {
  const location = useLocation();

  return useMemo(() => {
    const { pathname } = location;

    return (
      buildCustomerBreadcrumb(pathname, options.customerId, options.customerName) ||
      buildSiteBreadcrumb(
        pathname,
        options.customerId,
        options.customerName,
        options.siteId,
        options.siteName
      ) ||
      buildVirtualTenantBreadcrumb(pathname, options) ||
      (pathname.includes('/alarm/edit/') && options.alarmId
        ? [
            { label: 'Home', path: '/' },
            {
              label: `Alarm ${options.alarmId}`,
              path: `/alarm/edit/${options.alarmId}`,
              isActive: true,
            },
          ]
        : null) ||
      (options.currentPageTitle
        ? [
            { label: 'Home', path: '/' },
            { label: options.currentPageTitle, isActive: true },
          ]
        : null) ||
      buildSimpleBreadcrumb(pathname, '/reports', 'Reports') ||
      buildSimpleBreadcrumb(pathname, '/alarm-estate', 'Alarm Estate View') ||
      buildSimpleBreadcrumb(pathname, '/dashboard', 'Dashboard') ||
      buildSimpleBreadcrumb(pathname, '/analyze', 'Analyze') ||
      buildManageBreadcrumb(pathname) ||
      buildSimpleBreadcrumb(pathname, '/admin', 'Admin') ||
      buildSimpleBreadcrumb(pathname, '/documentation', 'Documentation') ||
      buildSimpleBreadcrumb(pathname, '/comms', 'Communications') ||
      (pathname === '/' ? [{ label: 'Home', path: '/' }] : [])
    );
  }, [location.pathname, options]);
}
